import { useEffect, useRef, useState } from 'react';
import { Chip } from '@blacknbrownstudios/ui';
import { BackendStatusBadge } from './BackendStatusBadge';
import { useChatStore } from '../stores/chat';
import { getBackendStatus, listModels } from '../api/client';
import type { BackendStatus, ModelInfo } from '../types';

type CatalogView = 'grouped' | 'flat';

interface ModelDisplay {
  id: string;
  name: string;
  description: string;
  provider: string;
  group: string;
  isCurrent: boolean;
  labels: string[];
}

const VIEW_STORAGE_KEY = 'binks-chat:model-catalog-view';

function formatProvider(provider: string): string {
  return provider.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatModelName(name: string): string {
  return name.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function buildDescription(model: ModelInfo): string {
  const tags = [...model.labels];

  if (model.capabilities.code) {
    tags.push('code');
  }
  if (model.capabilities.reasoning) {
    tags.push('reasoning');
  }
  if (model.capabilities.vision) {
    tags.push('vision');
  }
  if (model.capabilities.tools) {
    tags.push('tools');
  }

  return tags.join(' · ') || model.provider_model_id;
}

function getStoredView(): CatalogView {
  if (typeof window === 'undefined') {
    return 'grouped';
  }

  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return stored === 'flat' ? 'flat' : 'grouped';
}

export function ModelSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [models, setModels] = useState<ModelDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [catalogView, setCatalogView] = useState<CatalogView>(getStoredView);
  const [backendStatus, setBackendStatus] = useState<BackendStatus>({
    state: 'checking',
    message: 'Checking backend...',
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { selectedModel, setSelectedModel } = useChatStore();

  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, catalogView);
  }, [catalogView]);

  useEffect(() => {
    async function fetchModels() {
      try {
        setLoading(true);
        setBackendStatus({
          state: 'checking',
          message: 'Checking backend...',
        });

        const status = await getBackendStatus();
        setBackendStatus(status);

        if (status.state !== 'ready') {
          setModels([]);
          return;
        }

        const response = await listModels();

        const displayModels: ModelDisplay[] = response.models.map((model) => ({
          id: model.id,
          name: model.display_name || formatModelName(model.provider_model_id || model.id),
          description: buildDescription(model),
          provider: formatProvider(model.provider),
          group: formatProvider(model.group || model.provider),
          isCurrent: model.id === response.current || model.is_default,
          labels: model.labels,
        }));

        setModels(displayModels);

        if (!selectedModel && response.current) {
          setSelectedModel(response.current);
        }
      } catch (error) {
        const detail = error instanceof Error ? error.message : 'Failed to load models';
        console.error('Failed to fetch models:', error);
        setModels([]);
        setBackendStatus({
          state: 'models_unavailable',
          message: 'Backend reachable, models unavailable',
          detail,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchModels();
  }, [selectedModel, setSelectedModel]);

  const currentModel =
    models.find((model) => model.id === selectedModel) ||
    models.find((model) => model.isCurrent) ||
    models[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (loading) {
    return (
      <div className="space-y-2">
        <BackendStatusBadge status={backendStatus} />
        <div className="px-3 py-2 text-sm text-neutral-500">
          <span className="animate-pulse">Loading models...</span>
        </div>
      </div>
    );
  }

  if (backendStatus.state !== 'ready') {
    const label =
      backendStatus.state === 'backend_unreachable' ? 'Backend offline' : 'Models unavailable';

    return (
      <div className="space-y-2">
        <BackendStatusBadge status={backendStatus} />
        <div
          className="px-3 py-2 text-sm text-red-400"
          title={backendStatus.detail || backendStatus.message}
        >
          {label}
        </div>
      </div>
    );
  }

  const groupedModels = models.reduce<Record<string, ModelDisplay[]>>((accumulator, model) => {
    const group = model.group || model.provider;
    accumulator[group] = accumulator[group] || [];
    accumulator[group].push(model);
    return accumulator;
  }, {});

  const sortedGroups = Object.entries(groupedModels).sort(([left], [right]) =>
    left.localeCompare(right)
  );

  const renderModelOption = (model: ModelDisplay) => {
    const isSelected = model.id === selectedModel;

    return (
      <button
        key={model.id}
        onClick={() => {
          setSelectedModel(model.id);
          setIsOpen(false);
        }}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded text-left transition-colors ${
          isSelected ? 'bg-primary-900/30 text-white' : 'hover:bg-neutral-700/50 text-neutral-300'
        }`}
      >
        <div
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            isSelected ? 'border-primary-500' : 'border-neutral-600'
          }`}
        >
          {isSelected && <div className="w-2 h-2 rounded-full bg-primary-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{model.name}</div>
          <div className="text-xs text-neutral-500 truncate">
            {model.provider} · {model.description}
          </div>
        </div>
        {model.isCurrent && (
          <Chip label="default" size="small" color="primary" variant="outlined" />
        )}
      </button>
    );
  };

  return (
    <div className="space-y-2">
      <BackendStatusBadge status={backendStatus} />

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-neutral-300 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors"
          title="Select model"
        >
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span className="truncate">{currentModel?.name || 'Select model'}</span>
          </div>
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl z-50 max-h-72 overflow-y-auto">
            <div className="p-2 border-b border-neutral-700 space-y-2">
              <div>
                <div className="text-xs text-neutral-400 font-medium">Select Model</div>
                <div className="text-xs text-neutral-500">
                  Switch between provider-grouped and flat catalog views
                </div>
              </div>
              <div className="flex gap-1 rounded-md bg-neutral-900/60 p-1">
                <button
                  onClick={() => setCatalogView('grouped')}
                  className={`flex-1 rounded px-2 py-1 text-xs transition-colors ${
                    catalogView === 'grouped'
                      ? 'bg-neutral-700 text-white'
                      : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  By provider
                </button>
                <button
                  onClick={() => setCatalogView('flat')}
                  className={`flex-1 rounded px-2 py-1 text-xs transition-colors ${
                    catalogView === 'flat'
                      ? 'bg-neutral-700 text-white'
                      : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  Flat
                </button>
              </div>
            </div>

            <div className="p-1">
              {catalogView === 'flat' && models.map((model) => renderModelOption(model))}

              {catalogView === 'grouped' &&
                sortedGroups.map(([group, groupModels]) => (
                  <div key={group} className="pb-1">
                    <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                      {group}
                    </div>
                    {groupModels.map((model) => renderModelOption(model))}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
